import React, {useMemo, useState} from "react";
import {Box, LinearProgress, Menu, MenuItem} from "@mui/material";
import {useQuery} from "@apollo/client";
import moment from "moment-timezone";

import {SHIFT_ADMIN_PERFORMER_ACTIVITY} from "../queries/GraphQLQueries";
import {useTimezone} from "../hooks/useTimezone";
import {
    calculateNetAmendmentDurationInMinutes,
    calculateTotalShiftDurationIncludingCheckInTimeInMinutes
} from "../helpers/ShiftHelper";
import {buildMonthDateRangeIso} from "../helpers/DateTimeRangeHelper";
import ShiftAdminUtils from "../helpers/ShiftAdminUtils";
import {SHIFT_ADMIN_ALL_FILTER_VALUE, SHIFT_ADMIN_VIEWS} from "../constants/ShiftAdminConstants";
import {YEAR_MONTH_FORMAT} from "../config/DateTimeConstants";
import ShiftAdminToolbar from "../components/shift-admin/ShiftAdminToolbar";
import ShiftAdminSummaryHeader from "../components/shift-admin/ShiftAdminSummaryHeader";
import ShiftAdminPerformerGrid from "../components/shift-admin/ShiftAdminPerformerGrid";
import ShiftAdminAddAmendmentDialog from "../components/shift-admin/ShiftAdminAddAmendmentDialog";

const EARLIEST_SHIFT_HISTORY_MONTH = process.env.REACT_APP_EARLIEST_SHIFT_HISTORY_MONTH;

const styles = {
    page: {
        px: 2,
        pb: 2
    },
    progressTrack: {
        width: '100%',
        height: '0.25rem',
    }
};

export default function ShiftAdminView() {
    const {timezone} = useTimezone();
    const [activeView, setActiveView] = useState(SHIFT_ADMIN_VIEWS.MONTH);
    const [currentDate, setCurrentDate] = useState(moment().tz(timezone));
    const [selectedServiceFilter, setSelectedServiceFilter] = useState(SHIFT_ADMIN_ALL_FILTER_VALUE);
    const [selectedPerformerFilter, setSelectedPerformerFilter] = useState(SHIFT_ADMIN_ALL_FILTER_VALUE);
    const [addPerformerMenuAnchorElement, setAddPerformerMenuAnchorElement] = useState(null);
    const [openAddAmendmentDialog, setOpenAddAmendmentDialog] = useState(false);
    const [selectedPerformer, setSelectedPerformer] = useState(null);
    const earliestNavigableDate = useMemo(() => moment.tz(EARLIEST_SHIFT_HISTORY_MONTH, YEAR_MONTH_FORMAT, timezone).startOf("month"), [timezone]);

    const {startTime: fromTime, endTime: toTime} = useMemo(
        () => buildMonthDateRangeIso(currentDate, timezone),
        [currentDate, timezone]
    );

    const {data, loading, refetch} = useQuery(SHIFT_ADMIN_PERFORMER_ACTIVITY, {variables: {fromTime, toTime, includeOnlyAccreditedServiceTypes: true}, fetchPolicy: "network-only"});

    const performerRows = useMemo(() => {
        const users = data?.users_filtered || [];

        return users.map(user => {
            const monthShifts = (user.availability || []).filter(shift => {
                const shiftStartTime = shift?.from;
                return shiftStartTime && moment(shiftStartTime).tz(timezone).isSame(currentDate, "month");
            });

            const monthStandaloneAmendments = (user.amendments || []).filter(amendment => !amendment?.shift?.id);

            if (monthShifts.length === 0 && monthStandaloneAmendments.length === 0) {
                return null;
            }

            const shiftIds = monthShifts.map(shift => shift.id);
            const monthShiftAmendments = (user.amendments || []).filter(amendment => shiftIds.includes(amendment?.shift?.id));
            const monthAmendments = [...monthShiftAmendments, ...monthStandaloneAmendments];

            const shiftMinutes = calculateTotalShiftDurationIncludingCheckInTimeInMinutes(monthShifts);

            const amendmentMinutes = calculateNetAmendmentDurationInMinutes(monthAmendments);
            const serviceKeycodes = monthShifts.flatMap((shift) => ShiftAdminUtils.mapShiftServiceKeycodes(shift));

            return {
                performerId: user.id,
                fullName: `${user?.givenName} ${user?.familyName}`,
                shifts: monthShifts,
                amendments: monthAmendments,
                services: [...new Set(serviceKeycodes)],
                shiftTimeRanges: ShiftAdminUtils.buildShiftTimeRanges(monthShifts, timezone),
                totalShiftCount: monthShifts.length,
                totalAmendmentCount: monthAmendments.length,
                totalMinutesIncludingAmendments: shiftMinutes + amendmentMinutes
            };
        })
            .filter(Boolean)
            .sort((left, right) => left.fullName.localeCompare(right.fullName));
    }, [data, currentDate, timezone]);

    const serviceFilterOptions = useMemo(() => {
        const serviceSet = new Set();
        performerRows.forEach(row => row.services.forEach(service => serviceSet.add(service)));
        return [SHIFT_ADMIN_ALL_FILTER_VALUE, ...Array.from(serviceSet).sort()];
    }, [performerRows]);

    const filteredMonthRows = useMemo(() => {
        return performerRows.filter(row => {
            const serviceFilterMatches = ShiftAdminUtils.doesRowMatchServiceFilter(row, selectedServiceFilter);
            const performerFilterMatches = selectedPerformerFilter === SHIFT_ADMIN_ALL_FILTER_VALUE || row.performerId === selectedPerformerFilter;

            return serviceFilterMatches && performerFilterMatches;
        }).map(row => {
            const serviceFilteredMetrics = ShiftAdminUtils.buildServiceFilteredRowMetrics(row, selectedServiceFilter, timezone);

            return {
                ...row,
                ...serviceFilteredMetrics,
                totalAmendmentCount: serviceFilteredMetrics.amendmentCount
            };
        });
    }, [performerRows, selectedServiceFilter, selectedPerformerFilter, timezone]);

    const dayRows = useMemo(() => {
        const selectedDay = moment(currentDate).tz(timezone);

        return performerRows
            .map(row => {
                const dailyShifts = row.shifts.filter(shift => {
                    const shiftStart = shift?.from;
                    return shiftStart && moment(shiftStart).tz(timezone).isSame(selectedDay, "day");
                });

                const dailyStandaloneAmendments = row.amendments.filter(amendment => {
                    return !amendment?.shift?.id
                        && amendment?.startTime
                        && moment(amendment.startTime).tz(timezone).isSame(selectedDay, "day");
                });

                if (dailyShifts.length === 0 && dailyStandaloneAmendments.length === 0) {
                    return null;
                }

                const shiftIds = dailyShifts.map(shift => shift.id);
                const dailyShiftAmendments = row.amendments.filter(amendment => shiftIds.includes(amendment?.shift?.id));
                const dailyAmendments = [...dailyShiftAmendments, ...dailyStandaloneAmendments];

                const shiftMinutes = calculateTotalShiftDurationIncludingCheckInTimeInMinutes(dailyShifts);

                const amendmentMinutes = calculateNetAmendmentDurationInMinutes(dailyAmendments);
                const dailyServiceKeycodes = dailyShifts.flatMap((shift) => ShiftAdminUtils.mapShiftServiceKeycodes(shift));

                return {
                    performerId: row.performerId,
                    fullName: row.fullName,
                    shifts: dailyShifts,
                    services: [...new Set(dailyServiceKeycodes)],
                    amendments: dailyAmendments,
                    totalShiftCount: dailyShifts.length,
                    shiftTimeRanges: ShiftAdminUtils.buildShiftTimeRanges(dailyShifts, timezone),
                    amendmentCount: dailyAmendments.length,
                    totalMinutesIncludingAmendments: shiftMinutes + amendmentMinutes
                };
            })
            .filter(Boolean)
            .sort((left, right) => left.fullName.localeCompare(right.fullName));
    }, [performerRows, currentDate, timezone]);

    const filteredDayRows = useMemo(() => {
        return dayRows.filter(row => {
            const serviceFilterMatches = ShiftAdminUtils.doesRowMatchServiceFilter(row, selectedServiceFilter);
            const performerFilterMatches = selectedPerformerFilter === SHIFT_ADMIN_ALL_FILTER_VALUE || row.performerId === selectedPerformerFilter;
            return serviceFilterMatches && performerFilterMatches;
        }).map(row => {
            const serviceFilteredMetrics = ShiftAdminUtils.buildServiceFilteredRowMetrics(row, selectedServiceFilter, timezone);

            return {
                ...row,
                ...serviceFilteredMetrics
            };
        });
    }, [dayRows, selectedServiceFilter, selectedPerformerFilter, timezone]);

    const addPerformerMenuOptions = useMemo(() => {
        const users = data?.users_filtered || [];

        return ShiftAdminUtils.buildAddPerformerMenuOptions(users, filteredDayRows);
    }, [data, filteredDayRows]);

    const isPreviousDateNavigationDisabled = moment(currentDate)
        .tz(timezone)
        .isSameOrBefore(earliestNavigableDate, activeView);

    const goToPrevious = () => {
        if (isPreviousDateNavigationDisabled) {
            return;
        }

        setCurrentDate(previousDate => moment(previousDate).tz(timezone).subtract(1, activeView));
    };

    const goToNext = () => {
        setCurrentDate(previousDate => moment(previousDate).tz(timezone).add(1, activeView));
    };

    const handleDateChange = (nextDate) => {
        if (!nextDate) {
            return;
        }

        const nextActiveDate = moment(nextDate)
            .tz(timezone)
            .startOf(activeView);

        setCurrentDate(nextActiveDate);
    };

    const handleViewChange = (_, nextView) => {
        if (!nextView) {
            return;
        }

        setActiveView(nextView);
    };

    const performersForDropdown = performerRows
        .filter(row => ShiftAdminUtils.doesRowMatchServiceFilter(row, selectedServiceFilter))
        .map(row => ({
            id: row.performerId,
            fullName: row.fullName
        }));

    const handleCloseAddAmendmentDialog = () => {
        setOpenAddAmendmentDialog(false);
        setSelectedPerformer(null);
    };

    const handleSelectAddPerformer = (performer) => {
        setSelectedPerformer(performer);
        setAddPerformerMenuAnchorElement(null);
        setOpenAddAmendmentDialog(true);
    };

    const handleServiceFilterChange = (event) => {
        const nextServiceFilter = event.target.value;
        setSelectedServiceFilter(nextServiceFilter);

        const isSelectedPerformerStillVisible = performerRows.some(row =>
            row.performerId === selectedPerformerFilter
            && ShiftAdminUtils.doesRowMatchServiceFilter(row, nextServiceFilter));

        if (!isSelectedPerformerStillVisible) {
            setSelectedPerformerFilter(SHIFT_ADMIN_ALL_FILTER_VALUE);
        }
    };

    const isMonthView = activeView === SHIFT_ADMIN_VIEWS.MONTH;

    const dateNavigation = {
        currentDate,
        onDateChange: handleDateChange,
        onGoPrevious: goToPrevious,
        onGoNext: goToNext,
        earliestNavigableDate,
        isPreviousDateNavigationDisabled
    };

    const serviceFilter = {
        selectedServiceFilter,
        onServiceFilterChange: handleServiceFilterChange,
        serviceFilterOptions
    };

    const performerFilter = {
        selectedPerformerFilter,
        onPerformerFilterChange: (event) => setSelectedPerformerFilter(event.target.value),
        performersForDropdown
    };

    return (
        <Box sx={styles.page}>
            <ShiftAdminToolbar
                activeView={activeView}
                onViewChange={handleViewChange}
                dateNavigation={dateNavigation}
                serviceFilter={serviceFilter}
                performerFilter={performerFilter}/>

            <Box sx={styles.progressTrack}>
                {
                    loading
                    &&
                    <LinearProgress/>
                }
            </Box>

            <ShiftAdminSummaryHeader
                currentDate={currentDate}
                monthRows={filteredMonthRows}
                dayRows={filteredDayRows}
                activeView={activeView}
                showAddPerformerButton={!isMonthView}
                onAddPerformerClick={(event) => setAddPerformerMenuAnchorElement(event.currentTarget)}
                isAddPerformerDisabled={addPerformerMenuOptions.length === 0}/>

            <ShiftAdminPerformerGrid
                rows={isMonthView ? filteredMonthRows : filteredDayRows}
                view={activeView}
                currentDate={currentDate}
                onAmendmentCreated={refetch}/>

            <Menu
                anchorEl={addPerformerMenuAnchorElement}
                open={!!addPerformerMenuAnchorElement}
                onClose={() => setAddPerformerMenuAnchorElement(null)}>
                {addPerformerMenuOptions.map((performer) => (
                    <MenuItem key={performer.id} onClick={() => handleSelectAddPerformer(performer)}>
                        {performer.fullName}
                    </MenuItem>
                ))}
            </Menu>

            <ShiftAdminAddAmendmentDialog
                open={openAddAmendmentDialog}
                onClose={handleCloseAddAmendmentDialog}
                performer={selectedPerformer}
                timezone={timezone}
                onAmendmentCreated={refetch}/>
        </Box>
    );
}